import React from "react";
import { IconButton } from "@mui/material";
import { useSnackbar } from "notistack";
import { useProductPriceRecalculate } from "hooks";
import { Product } from "types";
import { CustomTooltip, Loading } from "core";
import { CalculationIcon } from "modules/icons";
import { getPriceCalcFailedReasonName } from "modules/shared/options/PriceCalcFailedReasonOption";

type ProductRecalcColumnProps = {
  product: Product;
  className?: string;
};

/**
 * Neuberechnung einer einzelnen Karte.
 *
 * Der Ladezustand liegt absichtlich in dieser Zelle und nicht in der Tabelle: eine
 * "recalculatingProductID" weiter oben würde bei jedem Klick alle Zeilen zweimal
 * neu rendern.
 */
export const ProductRecalcColumn: React.FC<ProductRecalcColumnProps> = ({
  product,
  className,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const recalculateMutation = useProductPriceRecalculate();

  const recalculateHandler = async () => {
    const result = await recalculateMutation
      .mutateAsync({
        productID: product.id,
        expansionID: product.expansionID,
      })
      .catch((error: { errorType?: string; message?: string }) => {
        // Ein Cardmarket-Hänger erscheint als AppSync-Timeout. Ohne eigene Meldung
        // läse sich das für den Nutzer wie "keine Angebote gefunden".
        const message =
          error?.errorType === "Lambda:ExecutionTimeoutException"
            ? "Die Berechnung hat zu lange gedauert. Bitte erneut versuchen."
            : (error?.message ?? "Neuberechnung fehlgeschlagen.");

        enqueueSnackbar(message, { variant: "error" });

        return null;
      });

    if (!result) {
      return;
    }

    if (result.success) {
      const price = result.priceStore;

      enqueueSnackbar(
        price
          ? `Preis neu berechnet: ${price.toLocaleString("de-DE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} €`
          : "Preis neu berechnet.",
        { variant: "success" },
      );

      return;
    }

    // Fachlicher Fehlschlag: die Zeile bleibt rot, der Grund wird benannt.
    enqueueSnackbar(getPriceCalcFailedReasonName(result.failedReason), {
      variant: "warning",
    });
  };

  if (recalculateMutation.isPending) {
    return <Loading size="20px" style={{ margin: 0 }} />;
  }

  return (
    <CustomTooltip title="Preis dieser Karte neu berechnen">
      <span>
        <IconButton onClick={recalculateHandler} className={className}>
          <CalculationIcon />
        </IconButton>
      </span>
    </CustomTooltip>
  );
};
